import PropTypes from 'prop-types';
import React from 'react';
import { Record } from 'immutable';
import _ from 'lodash';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';

import { translate as t } from '../../lib/I18n';

import FeatureFlag from './FeatureFlag';
import Icon from './Icon';
import UploadButton from '../form/UploadButton';
import { getWidthDifferenceToReachRatio } from '../../records/Photo';
import Button from 'react-bootstrap/lib/Button';

class PhotoUpload extends React.PureComponent {
    static propTypes = {
        acceptedFormats: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
        bodyText: PropTypes.string,
        buttonText: PropTypes.string,
        footerText: PropTypes.string,
        footerTextFeatureFlag: PropTypes.string,
        form: PropTypes.instanceOf(Record).isRequired,
        field: PropTypes.string.isRequired,
        reset: PropTypes.bool,
        hideFormats: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        onDelete: PropTypes.func,
        orientation: PropTypes.oneOf(['portrait', 'landscape']),
        placeholderIcon: PropTypes.string,
        uploadConditions: PropTypes.array,
        validation: PropTypes.object,
        visited: PropTypes.bool,
        existingPhoto: PropTypes.oneOf(['object', 'string']),
        onUploadClick: PropTypes.func,
        isDisabled: PropTypes.bool,
        submitted: PropTypes.bool,
    };

    static defaultProps = {
        acceptedFormats: ['image/jpeg', 'image/jpg'],
        buttonText: t('photoupload.buttontext'),
        reset: false,
        hideFormats: false,
        orientation: 'portrait',
        placeholderIcon: '',
        existingPhoto: null,
        onUploadClick: null,
        isDisabled: false,
        onDelete: () => {},
    };

    state = {
        fileName: null,
        image: null,
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.props.reset && nextProps.reset) {
            this.setState({ fileName: null });
        }
    }

    _handlePhotoUpdate = event => {
        const { value, files } = event.target;
        const selectedImage = _.head(files);
        this.setState({ fileName: selectedImage.name });
        let photo = this.props.form.get(this.props.field).set('photo', value);

        if (selectedImage && window.FileReader) {
            const reader = new FileReader();
            const bufferReader = new FileReader();

            reader.onload = readerEvent => {
                const imageData = readerEvent.target.result;
                const image = new Image();

                image.src = imageData;
                image.onload = () => {
                    photo = photo.merge({
                        selectedImage,
                        imageData,
                        imageWidth: image.width,
                        imageHeight: image.height,
                    });

                    this.setState({ image: { width: image.width, height: image.height } });
                    bufferReader.readAsArrayBuffer(selectedImage);
                };
            };

            bufferReader.onload = () => {
                const binary = bufferReader.result;
                photo = photo.merge({
                    binary,
                });
                this.props.onChange(this.props.field, photo);
            };

            reader.readAsDataURL(selectedImage);
        } else {
            photo = photo
                .remove('selectedImage')
                .remove('imageData')
                .remove('imageWidth')
                .remove('imageHeight');
            this.props.onChange(this.props.field, photo);
        }
    };

    _renderPhoto({ form, field }) {
        const { existingPhoto } = this.props;
        const { imageData } = form.get(field);

        if (imageData) {
            return (
                <div
                    className="smartcard-user-photo"
                    style={{ backgroundImage: `url(${imageData})` }}
                />
            );
        }

        if (existingPhoto) {
            return (
                <img
                    src={existingPhoto}
                    className="img-responsive"
                    alt="Existing smartcard portrait"
                />
            );
        }

        const { placeholderIcon } = this.props;
        return placeholderIcon ? <Icon icon={placeholderIcon} /> : null;
    }

    _renderFormatRequirements() {
        const { acceptedFormats, hideFormats } = this.props;

        if (Array.isArray(acceptedFormats) && acceptedFormats.length && !hideFormats) {
            return (
                <li>
                    {t('photoupload.format-requirements-label').concat(acceptedFormats.join(', '))}
                </li>
            );
        } else {
            return null;
        }
    }

    _getFileErrors({ submitted, validation, visited }) {
        const { image } = this.state;

        if (submitted && validation['photo.imageData']) {
            return t('photoupload.error.required');
        } else if (visited && validation['photo.photo']) {
            return t('photoupload.error.required');
        } else if (validation.fileSize) {
            return t('photoupload.error.fileSize');
        } else if (validation.imageOrientation) {
            if (image) {
                return t('photoupload.error.imageOrientationWithDifference', {
                    difference: getWidthDifferenceToReachRatio(
                        image.width,
                        image.height,
                        'portrait',
                    ),
                });
            } else {
                return t('photoupload.error.imageOrientation');
            }
        } else if (validation.fileFormat) {
            return t('photoupload.error.unacceptedFileFormat');
        } else if (validation.fileHeight) {
            return t('account.details.photoWrongDimensions');
        }

        return null;
    }

    render() {
        const {
            acceptedFormats,
            bodyText,
            field,
            form,
            orientation,
            uploadConditions,
            buttonText,
            footerTextFeatureFlag,
            footerText,
            reset,
            onDelete,
            onUploadClick,
            isDisabled,
            existingPhoto
        } = this.props;

        const error = this._getFileErrors(this.props);
        const { imageData } = form.get(field);

        const onUploadClickProp = onUploadClick ? { onUploadClick } : {};

        return (
            <Row>
                <Col md={12} className="margin-top--sm">
                    <Row>
                        <Col md={12} className="photo-upload-container">
                            <label htmlFor="smartcard-photo-upload">
                                <div className={`box box--rounded ${orientation}-upload`}>
                                    {this._renderPhoto(this.props)}
                                    {(existingPhoto || imageData) && typeof onDelete === 'function' && <Button className="photo-upload__delete hidden-print btn-plain" onClick={onDelete}>
                                        <span className="sr-only">{ t('order.summary.removeJourney') }</span>
                                        <Icon icon="bin" className="photo-upload__delete__icon" />
                                    </Button>}
                                </div>
                                {error && (
                                    <h3 className="alert alert-danger smartcard-passenger__error">
                                        {error}
                                    </h3>
                                )}
                                <UploadButton
                                    fileName={this.state.fileName}
                                    error={error}
                                    {...onUploadClickProp}
                                    accept={acceptedFormats}
                                    onChange={this._handlePhotoUpdate}
                                    reset={reset}
                                    isDisabled={isDisabled}
                                    className="photo-upload__btn"
                                    testId="photo-upload"
                                >
                                    {buttonText}
                                </UploadButton>
                            </label>

                            <div>
                                <p>{bodyText}</p>

                                {uploadConditions && (
                                    <ul className={`list-styled ${orientation}-upload--conditions`}>
                                        {uploadConditions.map(condition => (
                                            <li key={condition}>{condition}</li>
                                        ))}
                                        {this._renderFormatRequirements()}
                                    </ul>
                                )}
                            </div>
                        </Col>
                    </Row>
                    {footerTextFeatureFlag && footerText && (
                        <FeatureFlag name={footerTextFeatureFlag}>
                            <Row>
                                <Col md={12}>
                                    <p className="margin-top--sm text-size--normal">{footerText}</p>
                                </Col>
                            </Row>
                        </FeatureFlag>
                    )}
                </Col>
            </Row>
        );
    }
}

export default PhotoUpload;
